import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '../lib/utils';
import { validateEmail, validatePhone, validateSiret, validatePassword } from '../lib/utils';
import { StepIndicator } from '../components/registration/StepIndicator';
import { BusinessForm } from '../components/registration/BusinessForm';
import { ResponsibleForm } from '../components/registration/ResponsibleForm';
import { SecurityForm } from '../components/registration/SecurityForm';
import { ConfirmationStep } from '../components/registration/ConfirmationStep';
import { ErrorMessage } from '../components/registration/ErrorMessage';
import { Button } from '../components/ui/Button';

const STEPS = ['Entreprise', 'Recruteur', 'Sécurité', 'Confirmation'] as const;

interface BusinessForm {
  company_name: string;
  phone_number: string;
  email: string;
  company_address: string;
  siret_number: string;
  legal_structure: string;
  area: string;
  logo?: string | null;
}

interface ResponsibleForm {
  full_name: string;
  position: string;
  phone_number: string;
  email: string;
}

interface SecurityForm {
  password: string;
  confirm_password: string;
  terms_accepted: boolean;
}

export function Register() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [businessId, setBusinessId] = useState<number | null>(null);
  const [formErrors, setFormErrors] = useState<{
    [key: string]: { [key: string]: boolean }
  }>({
    business: {},
    responsible: {},
    security: {}
  });

  const [businessForm, setBusinessForm] = useState<BusinessForm>({
    company_name: '',
    phone_number: '',
    email: '',
    company_address: '',
    siret_number: '',
    legal_structure: '',
    area: '',
    logo: null,
  });

  const [responsibleForm, setResponsibleForm] = useState<ResponsibleForm>({
    full_name: '',
    position: '',
    phone_number: '',
    email: '',
  });

  const [securityForm, setSecurityForm] = useState<SecurityForm>({
    password: '',
    confirm_password: '',
    terms_accepted: false,
  });

  const validateBusinessForm = () => {
    const errors: { [key: string]: boolean } = {};
    const errorMessages: string[] = [];
    
    if (!businessForm.company_name.trim()) {
      errors.company_name = true;
      errorMessages.push("Le nom de l'entreprise est requis");
    }
    
    if (!validatePhone(businessForm.phone_number)) {
      errors.phone_number = true;
      errorMessages.push("Le numéro de téléphone n'est pas valide");
    }
    
    if (!validateEmail(businessForm.email)) {
      errors.email = true;
      errorMessages.push("L'adresse email n'est pas valide");
    }
    
    if (!businessForm.company_address.trim()) {
      errors.company_address = true;
      errorMessages.push("L'adresse est requise");
    }
    
    if (!validateSiret(businessForm.siret_number)) {
      errors.siret_number = true;
      errorMessages.push("Le numéro SIRET n'est pas valide");
    }
    
    if (!businessForm.legal_structure) {
      errors.legal_structure = true;
      errorMessages.push("La structure juridique est requise");
    }
    
    if (!businessForm.area) {
      errors.area = true;
      errorMessages.push("La région est requise");
    }
    
    setFormErrors(prev => ({ ...prev, business: errors }));
    
    return errorMessages.length > 0 ? errorMessages.join('\n') : null;
  };

  const validateResponsibleForm = () => {
    const errors: { [key: string]: boolean } = {};
    const errorMessages: string[] = [];
    
    if (!responsibleForm.full_name.trim()) {
      errors.full_name = true;
      errorMessages.push("Le nom complet est requis");
    }
    
    if (!responsibleForm.position) {
      errors.position = true;
      errorMessages.push("Le poste est requis");
    }
    
    if (!validatePhone(responsibleForm.phone_number)) {
      errors.phone_number = true;
      errorMessages.push("Le numéro de téléphone n'est pas valide");
    }
    
    if (!validateEmail(responsibleForm.email)) {
      errors.email = true;
      errorMessages.push("L'adresse email n'est pas valide");
    }
    
    setFormErrors(prev => ({ ...prev, responsible: errors }));
    
    return errorMessages.length > 0 ? errorMessages.join('\n') : null;
  };

  const validateSecurityForm = () => {
    const errors: { [key: string]: boolean } = {};
    const errorMessages: string[] = [];
    
    if (!validatePassword(securityForm.password)) {
      errors.password = true;
      errorMessages.push("Le mot de passe ne respecte pas les critères de sécurité");
    }
    
    if (securityForm.password !== securityForm.confirm_password) {
      errors.confirm_password = true;
      errorMessages.push("Les mots de passe ne correspondent pas");
    }
    
    if (!securityForm.terms_accepted) {
      errors.terms_accepted = true;
      errorMessages.push("Vous devez accepter les conditions d'utilisation");
    }
    
    setFormErrors(prev => ({ ...prev, security: errors }));
    
    return errorMessages.length > 0 ? errorMessages.join('\n') : null;
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      const cleanSiret = businessForm.siret_number.replace(/\D/g, '');

      const requestData = {
        company_name: businessForm.company_name,
        phone_number: businessForm.phone_number,
        email: businessForm.email,
        company_address: businessForm.company_address,
        siret_number: String(cleanSiret),
        legal_structure: businessForm.legal_structure,
        area: businessForm.area,
        logo: businessForm.logo
      };

      console.log('Business Request Data:', requestData);

      const businessResponse = await fetch('https://x1lc-aku3-stjs.p7.xano.io/api:moW4brDB/business', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });

      console.log('Business Response Status:', businessResponse.status);
      const businessResponseText = await businessResponse.text();
      console.log('Business Response Text:', businessResponseText);

      if (!businessResponse.ok) {
        let errorData;
        try {
          errorData = JSON.parse(businessResponseText);
        } catch (e) {
          console.error('Failed to parse error response:', e);
          errorData = {};
        }
        throw new Error(errorData.message || "Erreur lors de la création de l'entreprise");
      }

      const businessResult = JSON.parse(businessResponseText);
      console.log('Business Result:', businessResult);
      setBusinessId(businessResult.id);

      const responsibleData = {
        ...responsibleForm,
        business_id: businessResult.id
      };
      console.log('Responsible Data:', responsibleData);

      const responsibleResponse = await fetch('https://x1lc-aku3-stjs.p7.xano.io/api:O20NksVB/responsibles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(responsibleData)
      });

      console.log('Responsible Response Status:', responsibleResponse.status);
      const responsibleResponseText = await responsibleResponse.text();
      console.log('Responsible Response Text:', responsibleResponseText);

      if (!responsibleResponse.ok) {
        let errorData;
        try {
          errorData = JSON.parse(responsibleResponseText);
        } catch (e) {
          console.error('Failed to parse error response:', e);
          errorData = {};
        }
        throw new Error(errorData.message || 'Erreur lors de la création du recruteur');
      }

      const authData = {
        email: responsibleForm.email,
        password: securityForm.password,
        name: responsibleForm.full_name,
        business_id: businessResult.id
      };
      console.log('Auth Data:', authData);

      const authResponse = await fetch('https://x1lc-aku3-stjs.p7.xano.io/api:O20NksVB/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(authData)
      });

      console.log('Auth Response Status:', authResponse.status);
      const authResponseText = await authResponse.text();
      console.log('Auth Response Text:', authResponseText);

      if (!authResponse.ok) {
        let errorData;
        try {
          errorData = JSON.parse(authResponseText);
        } catch (e) {
          console.error('Failed to parse error response:', e);
          errorData = {};
        }
        throw new Error(errorData.message || 'Erreur lors de la création du compte');
      }

      toast.success('Compte créé avec succès');
      setCurrentStep(STEPS.length - 1);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Une erreur est survenue';
      console.error('Error during submission:', err);
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    let validationError = null;

    switch (currentStep) {
      case 0:
        validationError = validateBusinessForm();
        break;
      case 1:
        validationError = validateResponsibleForm();
        break;
      case 2:
        validationError = validateSecurityForm();
        if (!validationError) {
          handleSubmit();
          return;
        }
        break;
    }

    if (validationError) {
      setError(validationError);
      toast.error(validationError);
      return;
    }

    setError(null);
    setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setError(null);
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Créez votre espace recruteur
          </h1>
          <p className="text-lg text-gray-600">
            Rejoignez notre plateforme et commencez à recruter les meilleurs talents
          </p>
        </div>

        <StepIndicator steps={STEPS} currentStep={currentStep} />

        <ErrorMessage message={error} />

        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transform transition-all duration-300 hover:shadow-md">
            <div className="p-8">
              {currentStep === 0 && (
                <BusinessForm 
                  form={businessForm} 
                  onChange={setBusinessForm} 
                  errors={formErrors.business} 
                />
              )}

              {currentStep === 1 && (
                <ResponsibleForm 
                  form={responsibleForm} 
                  onChange={setResponsibleForm} 
                  errors={formErrors.responsible} 
                />
              )}

              {currentStep === 2 && (
                <SecurityForm 
                  form={securityForm} 
                  onChange={setSecurityForm} 
                  errors={formErrors.security} 
                />
              )}

              {currentStep === 3 && (
                <ConfirmationStep />
              )}

              {currentStep < 3 && (
                <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                  <Button
                    variant="secondary"
                    onClick={handleBack}
                    disabled={currentStep === 0}
                    leftIcon={<ChevronLeft className="h-5 w-5" />}
                  >
                    Retour
                  </Button>

                  <Button
                    variant="primary"
                    onClick={handleNext}
                    isLoading={loading}
                    rightIcon={!loading && <ChevronRight className="h-5 w-5" />}
                    className="min-w-[120px]"
                  >
                    {!loading && (currentStep === 2 ? 'Créer le compte' : 'Suivant')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}